import { Injectable, Output } from "@angular/core";
import { EventEmitter } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { Flow } from "../obj/Flow";
import { Connection } from "../obj/Connection";

export type FlowProgressStatus = 'running' | 'done' | 'failed' | 'cancelled';

export interface FlowProgressItem {
    id: string;
    label: string;
    group?: string;
    status: FlowProgressStatus;
    completed?: number;
    total?: number;
    updatedAt: number;
}

export class FlowEvent {
    flow?: Flow;
    connection?: Connection;
    type: string;
    value: any = false;
    id?: any;
}

@Injectable({
    providedIn: 'root'
})
export class FlowEventService {

    openTabWindow$: EventEmitter<FlowEvent> = new EventEmitter<FlowEvent>();

    closeTabWindow$: EventEmitter<FlowEvent> = new EventEmitter<FlowEvent>();

    showSubscription$: EventEmitter<boolean> = new EventEmitter<boolean>();
    showFeedback$: EventEmitter<void> = new EventEmitter<void>();

    planUpdated$: EventEmitter<boolean> = new EventEmitter<boolean>();

    // when flows in storage are updated, this event tells UI to try to rebind
    flowsUpdated$: EventEmitter<boolean> = new EventEmitter<boolean>();

    // Header "Refreshed X ago" button → flows component listens and forces a re-scan.
    manualRefresh$: EventEmitter<void> = new EventEmitter<void>();

    // Header "Cancel" button → flows component listens and aborts the in-flight scan.
    cancelScan$: EventEmitter<void> = new EventEmitter<void>();

    // True while a scan (refreshEnvironment) is actively running. Drives the header
    // Cancel ↔ "Refreshed X ago" toggle. Distinct from flowManagementService.serviceStatus$
    // (which is a noisy free-form string and gets stuck on "Fetching Environments").
    scanLoading$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

    scanProgress$: BehaviorSubject<FlowProgressItem[]> = new BehaviorSubject<FlowProgressItem[]>([]);

    reportProgress(item: Omit<FlowProgressItem, 'updatedAt'> & { updatedAt?: number }): void {
        const current = this.scanProgress$.value || [];
        const nextItem: FlowProgressItem = {
            ...item,
            updatedAt: item.updatedAt || Date.now()
        };
        const existingIndex = current.findIndex((entry) => entry.id === nextItem.id);
        const next = existingIndex >= 0
            ? current.map((entry, index) => index === existingIndex ? nextItem : entry)
            : [nextItem, ...current];

        this.scanProgress$.next(
            next
                .sort((a, b) => b.updatedAt - a.updatedAt)
                .slice(0, 8)
        );
    }

    clearProgress(id: string): void {
        this.scanProgress$.next((this.scanProgress$.value || []).filter((entry) => entry.id !== id));
    }

    completeRunningProgress(): void {
        const now = Date.now();
        this.scanProgress$.next((this.scanProgress$.value || []).map((entry) => {
            if (entry.status !== 'running') {
                return entry;
            }
            return {
                ...entry,
                status: 'done',
                updatedAt: now
            };
        }));
    }

}
